import {
	Fragment,
	type Mark,
	type Node as PMNode,
	Slice,
} from '@atlaskit/editor-prosemirror/model';
import {
	AddMarkStep,
	AddNodeMarkStep,
	AttrStep,
	RemoveMarkStep,
	RemoveNodeMarkStep,
	ReplaceStep,
} from '@atlaskit/editor-prosemirror/transform';
import type { Step } from '@atlaskit/editor-prosemirror/transform-override';

import { diffBlockContent, diffInlineContent } from './content-diff';
import { sameMarks } from './marks';
import type { DiffContext } from './types';
import { filterMarks, nodeEqIgnoring } from './utils';

/**
 * Per-node dispatcher. `pos` is the position of THIS node in the parent doc
 * (or `-1` for the root doc node, whose content begins at position 0).
 */
export function createStepsForNode(
	oldNode: PMNode,
	newNode: PMNode,
	pos: number,
	ctx: DiffContext,
): Step[] {
	if (nodeEqIgnoring(oldNode, newNode, ctx.ignoreMarks)) return [];

	// Text vs text: handled by the inline diff path normally, but if we get here
	// (e.g. a doc whose top-level child IS a text node, unusual but possible in
	// custom schemas) just produce a Replace.
	if (oldNode.isText && newNode.isText) {
		if (oldNode.text === newNode.text) {
			// Same text, different marks - emit mark deltas via add/remove.
			return markDeltaSteps(
				oldNode.marks,
				newNode.marks,
				pos,
				pos + oldNode.nodeSize,
				/*isInline*/ true,
				ctx,
			);
		}
		return [new ReplaceStep(pos, pos + oldNode.nodeSize, new Slice(Fragment.from(newNode), 0, 0))];
	}

	if (oldNode.type !== newNode.type) {
		// Type change → replace the whole node. structure=false because the range
		// contains real content (the old node's body); structure=true would reject.
		if (pos < 0) {
			throw new Error('diffNodes: root doc node type mismatch');
		}
		return [new ReplaceStep(pos, pos + oldNode.nodeSize, new Slice(Fragment.from(newNode), 0, 0))];
	}

	// Same block type. Three orthogonal concerns:
	//   1. attrs   → AttrStep(s) on this node
	//   2. content → recurse (inline vs block path)
	//   3. marks   → AddNodeMark/RemoveNodeMark on this node
	const steps: Step[] = [];

	if (pos >= 0) {
		steps.push(...attrSteps(oldNode, newNode, pos));
	}

	if (oldNode.type.inlineContent) {
		steps.push(...diffInlineContent(oldNode.content, newNode.content, pos, ctx));
	} else {
		steps.push(...diffBlockContent(oldNode.content, newNode.content, pos, ctx));
	}

	if (pos >= 0) {
		steps.push(...nodeMarkDeltaSteps(oldNode.marks, newNode.marks, pos, ctx));
	}

	return steps;
}

function attrSteps(oldNode: PMNode, newNode: PMNode, pos: number): Step[] {
	const oldAttrs = oldNode.attrs ?? {};
	const newAttrs = newNode.attrs ?? {};
	const changed: Array<[string, unknown]> = [];
	const keys = new Set([...Object.keys(oldAttrs), ...Object.keys(newAttrs)]);
	for (const k of keys) {
		if (!compareDeep(oldAttrs[k], newAttrs[k])) {
			changed.push([k, newAttrs[k] ?? null]);
		}
	}
	return changed.map(([k, v]) => new AttrStep(pos, k, v));
}

// Mirrors prosemirror-model's compareDeep helper used for attribute equality.
function compareDeep(a: unknown, b: unknown): boolean {
	if (a === b) return true;
	if (!(a && typeof a === 'object') || !(b && typeof b === 'object')) return false;

	const isArray = Array.isArray(a);
	if (Array.isArray(b) !== isArray) return false;

	if (isArray) {
		const aArray = a as unknown[];
		const bArray = b as unknown[];
		if (aArray.length !== bArray.length) return false;
		for (let i = 0; i < aArray.length; i++) {
			if (!compareDeep(aArray[i], bArray[i])) return false;
		}
	} else {
		const aObject = a as Record<string, unknown>;
		const bObject = b as Record<string, unknown>;
		for (const prop in aObject) {
			if (!(prop in bObject) || !compareDeep(aObject[prop], bObject[prop])) return false;
		}
		for (const prop in bObject) {
			if (!(prop in aObject)) return false;
		}
	}

	return true;
}

function nodeMarkDeltaSteps(
	oldMarks: readonly Mark[],
	newMarks: readonly Mark[],
	pos: number,
	ctx: DiffContext,
): Step[] {
	const oldM = filterMarks(oldMarks, ctx.ignoreMarks);
	const newM = filterMarks(newMarks, ctx.ignoreMarks);
	if (sameMarks(oldM, newM)) return [];
	const steps: Step[] = [];
	for (const m of newM) {
		if (!oldM.some((om) => om.eq(m))) steps.push(new AddNodeMarkStep(pos, m));
	}
	for (const m of oldM) {
		if (!newM.some((nm) => nm.eq(m))) steps.push(new RemoveNodeMarkStep(pos, m));
	}
	return steps;
}

function markDeltaSteps(
	oldMarks: readonly Mark[],
	newMarks: readonly Mark[],
	from: number,
	to: number,
	isInline: boolean,
	ctx: DiffContext,
): Step[] {
	const oldM = filterMarks(oldMarks, ctx.ignoreMarks);
	const newM = filterMarks(newMarks, ctx.ignoreMarks);
	if (sameMarks(oldM, newM)) return [];
	const steps: Step[] = [];
	for (const m of newM) {
		if (!oldM.some((om) => om.eq(m))) {
			steps.push(isInline ? new AddMarkStep(from, to, m) : new AddNodeMarkStep(from, m));
		}
	}
	for (const m of oldM) {
		if (!newM.some((nm) => nm.eq(m))) {
			steps.push(isInline ? new RemoveMarkStep(from, to, m) : new RemoveNodeMarkStep(from, m));
		}
	}
	return steps;
}
